/**
 * UsersPage - User management page
 * Refactored to use extracted hooks and components
 */
import React, { useState, useMemo } from 'react';
import { MainLayout } from '../components/MainLayout';
import { Filter, Search, X, Plus, Edit, Trash2, Building2, VenetianMask } from 'lucide-react';
import { RoleBadge } from '../components/RoleBadge';
import useAuthStore from '../stores/authStore';
import VincularClientesUsuarioModal from '../components/VincularClientesUsuarioModal';
import PermissionGate from '../components/PermissionGate';
import { useUsersData } from '../hooks/useUsersData';
import { User, getUserDisplayName, getUserFullName } from '../types/user';
import { MRT_LOCALIZATION_PT_BR } from '../constants/mrtLocalization';
import {
  NewUserModal,
  EditUserModal,
  ResetPasswordModal,
  UsersFilterPanel,
} from '../components/users';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
} from 'mantine-react-table';
import { ActionIcon, Group } from '@mantine/core';
import { ROLE_OPTIONS } from '../constants/roles';

const UsersPage: React.FC = () => {

  // N90: Simular Permissão (só MASTER). Navega como o usuário-alvo (leitura).
  const currentUser = useAuthStore((s) => s.user);
  const iniciarSimulacao = useAuthStore((s) => s.iniciarSimulacao);
  const handleSimular = async (alvo: User) => {
    const nome = getUserFullName(alvo) || alvo.username;
    const ok = window.confirm(
      `Simular permissão de "${nome}"?\n\n` +
      'Você verá o sistema como esse usuário (menus, botões e dados dele), ' +
      'em modo SOMENTE LEITURA. A simulação expira em 30 minutos. ' +
      'Use "Encerrar simulação" para voltar ao seu acesso.'
    );
    if (!ok) return;
    try {
      await iniciarSimulacao(alvo.id);
    } catch (e: any) {
      window.alert('Não foi possível iniciar a simulação: ' + (e?.response?.data?.detail || e?.message || e));
    }
  };

  // Users data hook
  const {
    users,
    clientes,
    loading,
    totalCount,
    searchTerm,
    setSearchTerm,
    selectedRoles,
    setSelectedRoles,
    selectedStatus,
    setSelectedStatus,
    selectedClientes,
    setSelectedClientes,
    filterCount,
    clearFilters,
    handleAtualizar,
    handleCreateUser,
    handleExcluir,
    handleSaveEditedUser,
    handleResetPassword,
    ConfirmDialog,
  } = useUsersData();

  // UI states
  const [showFilters, setShowFilters] = useState(false);
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showVincularClientesModal, setShowVincularClientesModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Handlers
  const handleOpenEditModal = (user: User) => {
    setSelectedUser(user);
    setShowEditUserModal(true);
  };

  const handleOpenResetPassword = (user: User) => {
    setSelectedUser(user);
    setShowResetPasswordModal(true);
  };

  const handleOpenVincularClientes = (user: User) => {
    setSelectedUser(user);
    setShowVincularClientesModal(true);
  };

  // Table columns
  const columns = useMemo<MRT_ColumnDef<User>[]>(
    () => [
      {
        id: 'nome_completo',
        header: 'Nome Completo',
        accessorFn: (row) => getUserFullName(row),
        Cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
              {getUserFullName(row.original).charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {getUserFullName(row.original)}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                ID: {row.original.id}
              </span>
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'username',
        header: 'Nome',
        Cell: ({ row }) => (
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {row.original.username}
          </span>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
      },
      {
        accessorKey: 'role',
        header: 'Função',
        Cell: ({ cell }) => <RoleBadge role={cell.getValue<string>()} size="sm" />,
        filterVariant: 'select',
        filterSelectOptions: ROLE_OPTIONS,
      },
      {
        accessorKey: 'ativo',
        header: 'Status',
        Cell: ({ cell }) => (
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
              cell.getValue<boolean>()
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
            }`}
          >
            <div
              className={`w-2 h-2 rounded-full ${
                cell.getValue<boolean>() ? 'bg-green-500' : 'bg-red-500'
              }`}
            />
            {cell.getValue<boolean>() ? 'Ativo' : 'Inativo'}
          </span>
        ),
        filterVariant: 'select',
        filterSelectOptions: [
          { label: 'Ativo', value: 'true' },
          { label: 'Inativo', value: 'false' },
        ],
      },
      {
        id: 'vinculos',
        header: 'Vínculos',
        accessorFn: (row) => row.cliente_ids?.length || 0,
        Cell: ({ cell }) => (
          <div className="flex justify-center">
            <span className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
              <Building2 className="w-4 h-4" />
              {cell.getValue<number>()}
            </span>
          </div>
        ),
      },
    ],
    []
  );

  // Table configuration
  const table = useMantineReactTable({
    columns,
    data: users,
    enableColumnFilters: true,
    enableSorting: true,
    enablePagination: true,
    enableRowActions: true,
    positionActionsColumn: 'last',
    enableDensityToggle: false,
    initialState: {
      showColumnFilters: true,
      density: 'xs',
    },
    mantineTableProps: {
      highlightOnHover: true,
      striped: true,
      withTableBorder: true,
      withColumnBorders: true,
      sx: {
        'th': { padding: '8px 12px !important' },
        'td': { padding: '4px 12px !important' },
      },
    },
    renderRowActions: ({ row }) => (
      <Group gap={4} wrap="nowrap">
        {/* N90: Simular permissão — só MASTER, e não em si mesmo */}
        {currentUser?.role === 'MASTER' && row.original.id !== currentUser?.id && (
          <ActionIcon
            onClick={() => handleSimular(row.original)}
            variant="subtle"
            color="grape"
            size="sm"
            title="Simular permissão (ver como este usuário)"
          >
            <VenetianMask size={14} />
          </ActionIcon>
        )}
        <PermissionGate tela="USUARIOS_LIST" acao="editar">
          <ActionIcon
            onClick={() => handleOpenVincularClientes(row.original)}
            variant="subtle"
            color="blue"
            size="sm"
            title="Vínculos"
          >
            <Building2 size={14} />
          </ActionIcon>
        </PermissionGate>
        <PermissionGate tela="USUARIOS_LIST" acao="editar">
          <ActionIcon
            onClick={() => handleOpenEditModal(row.original)}
            variant="subtle"
            color="gray"
            size="sm"
            title="Editar"
          >
            <Edit size={14} />
          </ActionIcon>
        </PermissionGate>
        <PermissionGate tela="USUARIOS_LIST" acao="excluir">
          <ActionIcon
            onClick={() => handleExcluir(row.original)}
            variant="subtle"
            color="red"
            size="sm"
            title="Excluir"
          >
            <Trash2 size={14} />
          </ActionIcon>
        </PermissionGate>
      </Group>
    ),
    mantinePaperProps: {
      shadow: 'none',
      withBorder: true,
      radius: 'md',
    },
    localization: MRT_LOCALIZATION_PT_BR,
  });

  return (
    <>
      <ConfirmDialog />
      <MainLayout>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 mb-6 px-6 pt-6 shadow-sm">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Usuários do Sistema</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {totalCount} {totalCount === 1 ? 'usuário' : 'usuários'}
              </p>
            </div>
            <div className="flex gap-3 items-center">
              {/* Search */}
              <div className="relative hidden md:block w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar usuário..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-gray-700 dark:text-gray-300"
              >
                <Filter className="w-4 h-4" />
                Filtros
                {filterCount > 0 && (
                  <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full font-medium">
                    {filterCount}
                  </span>
                )}
              </button>

              {/* New User Button */}
              <PermissionGate tela="USUARIOS_LIST" acao="criar">
                <button
                  onClick={() => setShowNewUserModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Novo Usuário
                </button>
              </PermissionGate>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        <UsersFilterPanel
          isVisible={showFilters}
          searchTerm={searchTerm}
          selectedRoles={selectedRoles}
          selectedStatus={selectedStatus}
          selectedClientes={selectedClientes}
          clientes={clientes}
          filterCount={filterCount}
          onSearchChange={setSearchTerm}
          onRolesChange={setSelectedRoles}
          onStatusChange={setSelectedStatus}
          onClientesChange={setSelectedClientes}
          onClearAll={clearFilters}
        />

        {/* Data Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-0">
            <MantineReactTable table={table} />
          </div>
        </div>

        {/* Modals */}
        <NewUserModal
          isOpen={showNewUserModal}
          onClose={() => setShowNewUserModal(false)}
          onCreate={handleCreateUser}
        />

        <EditUserModal
          isOpen={showEditUserModal}
          user={selectedUser}
          onClose={() => {
            setShowEditUserModal(false);
            setSelectedUser(null);
          }}
          onSave={handleSaveEditedUser}
        />

        <ResetPasswordModal
          isOpen={showResetPasswordModal}
          user={selectedUser}
          onClose={() => {
            setShowResetPasswordModal(false);
            setSelectedUser(null);
          }}
          onReset={handleResetPassword}
        />

        {showVincularClientesModal && selectedUser && (
          <VincularClientesUsuarioModal
            usuarioId={selectedUser.id}
            usuarioNome={selectedUser.username}
            usuarioRole={selectedUser.role}
            onClose={() => {
              setShowVincularClientesModal(false);
              setSelectedUser(null);
            }}
            onSave={() => {
              setShowVincularClientesModal(false);
              setSelectedUser(null);
              handleAtualizar();
            }}
          />
        )}
      </MainLayout>
    </>
  );
};

export default UsersPage;
